import React, { Fragment, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Popover
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import moment from 'moment';

import { requestList as request } from 'api/sfms/mix/ratio';
import CheckPlant from 'components/common-sfims/CheckPlant';
import MonthPicker from 'components/common-sfims/form/MonthPicker';
import CommonList from 'components/common-sfims/List';
import ListInContent from 'components/common-sfims/ListInContent';
import columns from 'components/pages/sfms/mix/ratio/columns/list';
import Download from 'components/pages/sfms/mix/ratio/Download';
import Export from 'components/pages/sfms/mix/ratio/Export';
import Inform from 'components/pages/sfms/mix/ratio/Inform';
import Init from 'components/pages/sfms/mix/ratio/Init';
import Upload from 'components/pages/sfms/mix/ratio/Upload';
import UploadDiff from 'components/pages/sfms/mix/ratio/UploadDiff';
import { datas2ExcelJSON, utc2tz } from 'function/convert';
import { onListClick } from 'function/event';
import { downloadExcelByJSON } from 'function/file';
import { getText } from 'function/language';
import useCheckPlantState from 'global/checkPlantState';
import useListScroll from 'hooks/useListScroll';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RATIO_STATUS, RETURN_YN, SAP_STATUS } from 'types/type';
import { comma, number as formatNumber } from 'utils/format';
import format from 'utils/string/format';

/**
 * 배합비 등록 관리 > 배합비 - 목록
 */
const List = () => {
  /* hook */
  const { end, setEnd, total, setTotal } = useListScroll();

  /* component */
  // SAP 전송상태 표시 Tip
  const ExportTip = (
    <Popover>
      <Popover.Header as="h3">{getText('exportTip')}</Popover.Header>
      <Popover.Body>
        <CommonList
          className={{ table: 'fs--2' }}
          columns={[
            {
              text: 'display',
              className: 'pt-1 pb-1'
            },
            {
              text: 'completeDownloadSAP',
              className: 'pt-1 pb-1'
            },
            {
              text: 'completeExportSAP',
              className: 'pt-1 pb-1'
            }
          ]}
        >
          <tr>
            <td className="pt-1 pb-1">●</td>
            <td className="pt-1 pb-1">OK</td>
            <td className="pt-1 pb-1">OK</td>
          </tr>
          <tr>
            <td className="pt-1 pb-1">
              <span className="text-warning">▲</span>
            </td>
            <td className="pt-1 pb-1">OK</td>
            <td className="pt-1 pb-1">NO</td>
          </tr>
          <tr>
            <td className="pt-1 pb-1">{getText('no')}</td>
            <td className="pt-1 pb-1">NO</td>
            <td className="pt-1 pb-1">NO</td>
          </tr>
        </CommonList>
      </Popover.Body>
    </Popover>
  );

  /* router */
  const { state } = useLocation();

  /* recoil */
  const { checkPlant } = useCheckPlantState();

  /* state */
  // 목록
  const [datas, setDatas] = useState([]);
  // 로딩 여부
  const [isLoading, setIsLoading] = useState(true);
  // SAP용 다운로드 모달 표시 여부
  const [isShowDownload, setIsShowDownload] = useState(false);
  // SAP에 전송 모달 표시 여부
  const [isShowExport, setIsShowExport] = useState(false);
  // 정보 모달 표시 여부
  const [isShowInform, setIsShowInform] = useState(false);
  // 초기화 모달 표시 여부
  const [isShowInit, setIsShowInit] = useState(false);
  // 업로드 모달 표시 여부
  const [isShowUpload, setIsShowUpload] = useState(false);
  // 차이 표시 모달 표시 여부
  const [isShowUploadDiff, setIsShowUploadDiff] = useState(false);
  // 메시지
  const [message, setMessage] = useState({});
  // 코드 검색어
  const [mixCd, setMixCd] = useState(state?.mixCd ?? '');
  // 배합비 목록 조회 요청 데이터
  const [params, setParams] = useObjReducer({
    factoryCodeSeqList: checkPlant,
    mixYm: moment()
      .add(moment().date() >= 22 ? 1 : 0, 'months')
      .format('YYYYMM'),
    ...state
  });
  // 선택한 월
  const [mixYm, setMixYm] = useState(state?.mixYm ?? params.mixYm);
  // 선택한 번호
  const [selectSeq, setSelectSeq] = useState(0);
  // 배합비 업로드 요청 데이터
  const [uploadData, setUploadData] = useObjReducer({});

  /* watch */
  useEffect(() => {
    requestList();
  }, [params]);

  /* method */
  // 배합비 목록 조회 요청
  const requestList = async () => {
    setEnd(20);
    setTotal(0);
    setDatas([]);
    setIsLoading(true);
    setMessage({});

    try {
      const {
        list = [],
        totalCount = 0,
        code,
        message
      } = await request(params);

      if (code !== RESPONSE_CODE.Success) {
        throw {
          code,
          error: message
        };
      }

      setTotal(totalCount);
      setDatas(
        list.map((value = {}, index) => ({
          ...value,
          rowNum: totalCount - index,
          regDt: utc2tz(value.regDt),
          transDt: utc2tz(value.transDt)
        }))
      );
    } catch (error) {
      setMessage(error);
    } finally {
      setIsLoading(false);
    }
  };
  // 정보 모달 표시
  const showInform = seq => {
    setIsShowInform(true);
    setSelectSeq(seq);
  };

  /* event */
  // 엑셀 다운로드 클릭 시
  const onDownloadExcelClick = () => {
    downloadExcelByJSON(
      datas2ExcelJSON(datas, columns),
      [getText('mix'), moment(params.mixYm).format('YYMM')]
        .join('_')
        .concat('.xlsx')
    );
  };
  // 검색 클릭 시
  const onSearchClick = event => {
    event.preventDefault();

    setParams({
      factoryCodeSeqList: checkPlant,
      mixYm,
      mixCd
    });
  };
  // 업로드 클릭 시
  const onUploadClick = (ratioStatus = RATIO_STATUS.REGULAR) => {
    setUploadData({ ratioStatus });
    setIsShowUpload(true);
  };

  /* view */
  return (
    <>
      <ListInContent
        columns={Object.keys(columns).map(key =>
          key === 'sapStatus' ? (
            <>
              {getText(key)}
              <OverlayTrigger
                overlay={ExportTip}
                placement="top"
                trigger="click"
              >
                <span className="ms-1">
                  <FontAwesomeIcon
                    icon="info-circle"
                    className="cursor-pointer"
                  />
                </span>
              </OverlayTrigger>
            </>
          ) : (
            key
          )
        )}
        message={message}
        showLoading={isLoading}
      >
        <Fragment key="headerButton">
          <Button
            size="sm"
            className="ms-2"
            onClick={() => onUploadClick(RATIO_STATUS.REGULAR)}
          >
            {getText('uploadMixRegular')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => onUploadClick(RATIO_STATUS.MODIFY)}
          >
            {getText('uploadMixModify')}
          </Button>
          <Button
            size="sm"
            variant="danger"
            className="ms-2"
            onClick={() => setIsShowInit(true)}
          >
            {getText('init')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => setIsShowDownload(true)}
          >
            {getText('downloadSAP')}
          </Button>
          <Button
            size="sm"
            className="ms-2"
            onClick={() => setIsShowExport(true)}
          >
            {getText('exportSAP')}
          </Button>
        </Fragment>
        <Fragment key="search">
          <CheckPlant value={params.factoryCodeSeqList} />
          <Form onSubmit={onSearchClick}>
            <Row className="ms-0 me-0 mt-3">
              <Col sm={1} className="ps-1 pe-1">
                <MonthPicker value={mixYm} onUpdate={setMixYm} />
              </Col>
              <Col sm={1} className="ps-1 pe-1">
                <Form.Control
                  size="sm"
                  type="text"
                  value={mixCd}
                  autoFocus
                  className="w-100"
                  placeholder={getText('code')}
                  onChange={({ target: { value } }) => setMixCd(value)}
                />
              </Col>
              <Col
                sm="auto"
                className="ps-1 pe-1 d-flex flex-row align-items-center"
              >
                <Button disabled={isLoading} size="sm" type="submit">
                  {getText('search')}
                </Button>
                <p className="mb-0 ms-3 fs--1 fw-bold lh-1">
                  {format(getText('total'), { total: comma(total) })}
                </p>
                {datas.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="ms-2"
                    onClick={onDownloadExcelClick}
                  >
                    {getText('downloadExcel')}
                  </Button>
                )}
              </Col>
            </Row>
          </Form>
        </Fragment>
        <Fragment key="datas">
          {datas.slice(0, end).map((value = {}) => (
            <tr
              key={`datas${value.seq}`}
              className={classNames('cursor-pointer', {
                'text-danger': value.mixRt < 100,
                'bg-200': value.useYn !== RETURN_YN.Y
              })}
              onClick={event => onListClick(event, () => showInform(value.seq))}
            >
              {Object.entries(columns).map(([key, { type, format } = {}]) => (
                <td
                  key={`datas${value.seq}.${key}`}
                  className={classNames({ 'text-end': type === 'number' })}
                >
                  {key === 'sapStatus' ? (
                    value.sapStatus ? (
                      <span
                        className={classNames({
                          'text-warning':
                            value.sapStatus === SAP_STATUS.SAP_DOWN,
                          'text-primary':
                            value.sapStatus === SAP_STATUS.SAP_TRANS
                        })}
                      >
                        {value.sapStatus}
                      </span>
                    ) : (
                      value.sapStatus
                    )
                  ) : type === 'number' ? (
                    formatNumber(value[key], format)
                  ) : (
                    value[key]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </Fragment>
      </ListInContent>
      <Upload
        show={isShowUpload}
        uploadData={uploadData}
        setUploadData={setUploadData}
        onHide={() => setIsShowUpload(false)}
        onUpload={() => setIsShowUploadDiff(true)}
      />
      <UploadDiff
        show={isShowUploadDiff}
        uploadData={uploadData}
        onHide={() => setIsShowUploadDiff(false)}
        onUpload={requestList}
      />
      <Download
        show={isShowDownload}
        onHide={() => setIsShowDownload(false)}
        onDownload={requestList}
      />
      <Init
        show={isShowInit}
        onHide={() => setIsShowInit(false)}
        onInit={requestList}
      />
      <Export
        show={isShowExport}
        onHide={() => setIsShowExport(false)}
        onExport={requestList}
      />
      <Inform
        show={isShowInform}
        seq={selectSeq}
        onHide={() => setIsShowInform(false)}
      />
    </>
  );
};

export default List;
